import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  FileText, Upload, X, Bot, Send, ChevronRight, ChevronLeft, 
  Check, Image as ImageIcon, FileIcon, Paperclip, Loader, MessageSquare, AlertTriangle, ArrowLeft
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EstimateBuilder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [formType, setFormType] = useState(null); // null, 'quick', 'detailed', 'survey'
  const [currentStep, setCurrentStep] = useState(1);
  const [estimateId, setEstimateId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    project_name: '',
    project_type: 'Custom frames',
    description: '',
    budget_range: '',
    timeline: '',
    dimensions: '',
    materials_requested: '',
    specifications: '',
    technical_requirements: ''
  });
  
  // AI Chat
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef(null);
  
  // File uploads
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  const [saving, setSaving] = useState(false);

  const projectTypes = [
    'Trailer repairs',
    'Bracket fabrication', 
    'Custom frames',
    'Gate or fence repair',
    'Aluminum welding',
    'Stainless welding',
    'CNC plasma cutting',
    'Hole drilling & small machining tasks',
    'Custom signs (plasma cut)',
    'Off-road vehicle mods',
    'Structural posts/beams',
    'Handrail/stair rail fabrication',
    'Equipment repair',
    'Tool racks and carts',
    'Fire pits',
    'Food-grade stainless work',
    'Bending and forming (sheet metal)',
    'Machine guards',
    'Prototype components',
    'CAD/drawing services',
    'Other'
  ];

  const budgetRanges = [
    'Under $1,000', '$1,000 - $5,000', '$5,000 - $10,000',
    '$10,000 - $25,000', '$25,000 - $50,000', 'Over $50,000'
  ];

  const timelines = [
    'ASAP', 'Within 2 weeks', '2-4 weeks', '1-2 months', '2-3 months', 'Flexible'
  ];

  // Load existing estimate if editing
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const editId = urlParams.get('edit');
    
    if (editId) {
      setIsEditing(true);
      setEstimateId(editId);
      loadExistingEstimate(editId);
    }
  }, [location.search]);

  const loadExistingEstimate = async (id) => {
    try {
      const response = await axios.get(`${API}/estimates/requests/${id}`);
      const estimate = response.data;
      
      setFormData({
        project_name: estimate.project_name || '',
        project_type: estimate.project_type || 'Custom frames',
        description: estimate.description || '',
        budget_range: estimate.budget_range || '',
        timeline: estimate.timeline || '',
        dimensions: estimate.dimensions || '',
        materials_requested: estimate.materials_requested || '',
        specifications: estimate.specifications || '',
        technical_requirements: estimate.technical_requirements || ''
      });
      
      setFormType(estimate.form_type || 'quick');
      
      // Load associated files
      const filesResponse = await axios.get(`${API}/estimates/requests/${id}/files`);
      setFiles(filesResponse.data || []);
      
      toast.success('Estimate loaded for editing');
    } catch (error) {
      toast.error('Failed to load estimate');
      console.error('Load estimate error:', error);
      navigate('/portal');
    }
  };

  // Auto-create draft estimate when component loads
  useEffect(() => {
    const createDraftEstimate = async () => {
      if (!estimateId && formType) {
        const draftData = {
          form_type: formType,
          project_name: "Draft Estimate",
          project_type: projectTypes[0],
          description: "Draft estimate - customer is filling out details"
        };
        
        try {
          const response = await axios.post(`${API}/estimates/requests`, draftData);
          setEstimateId(response.data.id);
          console.log('Draft estimate created:', response.data.id);
        } catch (error) {
          console.error('Failed to create draft estimate:', error);
        }
      }
    };

    createDraftEstimate();
  }, [formType, estimateId]);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const handleCreateEstimate = async () => {
    if (!formData.project_name || !formData.description) {
      toast.error('Please fill in project name and description');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/estimates/requests`, {
        form_type: formType,
        ...formData
      });
      
      setEstimateId(response.data.id);
      toast.success('Estimate request created!');
      
      // Initialize AI chat
      if (chatMessages.length === 0) {
        setChatMessages([{
          role: 'assistant',
          content: `Hi! I'm your welding and fabrication assistant. I'm here to help you with your ${formData.project_name} project. Feel free to ask me anything about costs, timelines, materials, or requirements!`,
          timestamp: new Date().toISOString()
        }]);
      }
    } catch (error) {
      toast.error('Failed to create estimate request');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveProgress = async () => {
    if (!estimateId) {
      await handleCreateEstimate();
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/estimates/requests/${estimateId}`, formData);
      toast.success('Progress saved!');
    } catch (error) {
      toast.error('Failed to save progress');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitNew = async () => {
    // For new estimates - create and submit in one go
    setSaving(true);
    try {
      // First create the estimate
      const response = await axios.post(`${API}/estimates/requests`, {
        form_type: formType,
        ...formData
      });
      
      const newEstimateId = response.data.id;
      
      // Then submit it
      await axios.post(`${API}/estimates/requests/${newEstimateId}/submit`);
      toast.success('Estimate request submitted! Our team will review it shortly.');
      
      // Redirect to customer portal
      window.location.href = '/portal';
    } catch (error) {
      toast.error('Failed to submit estimate request');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!estimateId) {
      toast.error('Please create and save your estimate first');
      return;
    }

    try {
      await axios.post(`${API}/estimates/requests/${estimateId}/submit`);
      toast.success('Estimate request submitted! Our team will review it shortly.');
      // Redirect to customer portal
      window.location.href = '/portal';
    } catch (error) {
      toast.error('Failed to submit estimate request');
    }
  };

  const handleSendMessage = async () => {
    if (!chatInput.trim()) return;

    const userMessage = {
      role: 'user',
      content: chatInput,
      timestamp: new Date().toISOString()
    };

    setChatMessages(prev => [...prev, userMessage]);
    const currentInput = chatInput;
    setChatInput('');
    setChatLoading(true);

    try {
      // If no estimateId yet, create one first
      let currentEstimateId = estimateId;
      if (!currentEstimateId) {
        const draftData = {
          form_type: formType || 'quick',
          project_name: formData.project_name || "Draft Estimate",
          project_type: formData.project_type || projectTypes[0],
          description: formData.description || "Customer is getting assistance from AI"
        };
        
        const response = await axios.post(`${API}/estimates/requests`, draftData);
        currentEstimateId = response.data.id;
        setEstimateId(currentEstimateId);
      }

      const response = await axios.post(`${API}/estimates/ai/chat`, {
        estimate_id: currentEstimateId,
        message: currentInput,
        context: formData
      });

      const assistantMessage = {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString()
      };

      setChatMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      toast.error('Failed to send message');
      console.error(error);
    } finally {
      setChatLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length === 0) return;

    setUploading(true);

    try {
      // If no estimateId yet, create one first
      let currentEstimateId = estimateId;
      if (!currentEstimateId) {
        const draftData = {
          form_type: formType || 'quick',
          project_name: formData.project_name || "Draft Estimate",
          project_type: formData.project_type || projectTypes[0],
          description: formData.description || "Customer uploaded files"
        };
        
        const response = await axios.post(`${API}/estimates/requests`, draftData);
        currentEstimateId = response.data.id;
        setEstimateId(currentEstimateId);
      }

      for (const file of selectedFiles) {
        const fileFormData = new FormData();
        fileFormData.append('file', file);
        fileFormData.append('file_type', getFileType(file.name));

        try {
          const response = await axios.post(
            `${API}/estimates/requests/${currentEstimateId}/files?file_type=${getFileType(file.name)}`,
            fileFormData,
            {
              headers: { 'Content-Type': 'multipart/form-data' }
            }
          );

          setFiles(prev => [...prev, response.data]);
          toast.success(`${file.name} uploaded successfully`);
        } catch (error) {
          toast.error(`Failed to upload ${file.name}`);
        }
      }
    } catch (error) {
      toast.error('Failed to prepare file upload');
    } finally {
      setUploading(false);
    }
  };

  const getFileType = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    if (['pdf', 'dwg', 'dxf'].includes(ext)) return 'drawing';
    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return 'photo';
    if (['doc', 'docx', 'txt'].includes(ext)) return 'specification';
    return 'reference';
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'photo': return <ImageIcon size={16} />;
      case 'drawing': return <FileText size={16} />;
      default: return <FileIcon size={16} />;
    }
  };

  // Form Mode Selection
  if (!formType) {
    return (
      <div className="p-8">
        <div className="max-w-4xl mx-auto">
          {/* Header with Back Button */}
          <div className="flex items-center justify-between mb-8">
            <div className="text-center flex-1">
              <h1 className="text-4xl md:text-5xl font-black tracking-tight uppercase mb-2">
                Request an Estimate
              </h1>
              <p className="text-lg text-muted-foreground">
                Choose how you'd like to submit your project details
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/portal')}
              className="font-mono uppercase text-xs ml-4"
            >
              <ArrowLeft size={16} className="mr-2" />
              Back to Portal
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Quick Form */}
            <Card className="border-border hover:border-primary transition-colors cursor-pointer" onClick={() => setFormType('quick')}>
              <CardHeader>
                <CardTitle className="text-xl font-bold uppercase">
                  Quick Form
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Fast and simple. Perfect for straightforward projects.
                </p>
                <ul className="text-xs space-y-1 text-muted-foreground">
                  <li>• Basic project details</li>
                  <li>• 5-7 fields</li>
                  <li>• ~2 minutes</li>
                </ul>
                <Button className="w-full mt-4" variant="outline">
                  Start Quick Form
                </Button>
              </CardContent>
            </Card>

            {/* Detailed Form */}
            <Card className="border-border hover:border-primary transition-colors cursor-pointer" onClick={() => setFormType('detailed')}>
              <CardHeader>
                <CardTitle className="text-xl font-bold uppercase">
                  Detailed Form
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Comprehensive. For complex projects requiring specifics.
                </p>
                <ul className="text-xs space-y-1 text-muted-foreground">
                  <li>• Complete specifications</li>
                  <li>• 10-12 fields</li>
                  <li>• ~5 minutes</li>
                </ul>
                <Button className="w-full mt-4" variant="outline">
                  Start Detailed Form
                </Button>
              </CardContent>
            </Card>

            {/* Survey Mode */}
            <Card className="border-border hover:border-primary transition-colors cursor-pointer" onClick={() => setFormType('survey')}>
              <CardHeader>
                <CardTitle className="text-xl font-bold uppercase">
                  Guided Survey
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Step-by-step. We'll guide you through each section.
                </p>
                <ul className="text-xs space-y-1 text-muted-foreground">
                  <li>• 5-step process</li>
                  <li>• One section at a time</li>
                  <li>• ~5 minutes</li>
                </ul>
                <Button className="w-full mt-4" variant="outline">
                  Start Survey
                </Button>
              </CardContent>
            </Card>
          </div>

          <div className="mt-8 text-center">
            <p className="text-sm text-muted-foreground">
              💡 All forms include AI assistance and file upload capabilities
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Render form based on type
  const renderForm = () => {
    if (formType === 'survey') {
      return renderSurveyMode();
    } else {
      return renderStandardForm();
    }
  };

  const renderStandardForm = () => (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Main Form */}
      <div className="lg:col-span-2 space-y-6">
        <Card className="border-border">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-2xl font-bold uppercase">
                Project Details
              </CardTitle>
              <Badge variant="secondary" className="font-mono uppercase">
                {formType} form
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Project Name */}
            <div>
              <Label className="text-xs font-mono uppercase tracking-wide">
                Project Name *
              </Label>
              <Input
                value={formData.project_name}
                onChange={(e) => setFormData({ ...formData, project_name: e.target.value })}
                placeholder="e.g., Custom Steel Railing for Deck"
                className="mt-1"
              />
            </div>

            {/* Project Type */}
            <div>
              <Label className="text-xs font-mono uppercase tracking-wide">
                Project Type *
              </Label>
              <select
                value={formData.project_type}
                onChange={(e) => setFormData({ ...formData, project_type: e.target.value })}
                className="w-full mt-1 px-3 py-2 bg-background border border-input rounded-sm text-sm"
              >
                {projectTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            {/* Description */}
            <div>
              <Label className="text-xs font-mono uppercase tracking-wide">
                Project Description *
              </Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe your project in detail..."
                className="mt-1 min-h-[120px]"
              />
            </div>

            {/* Budget Range */}
            <div>
              <Label className="text-xs font-mono uppercase tracking-wide">
                Budget Range
              </Label>
              <select
                value={formData.budget_range}
                onChange={(e) => setFormData({ ...formData, budget_range: e.target.value })}
                className="w-full mt-1 px-3 py-2 bg-background border border-input rounded-sm text-sm"
              >
                <option value="">Select budget range...</option>
                {budgetRanges.map(range => (
                  <option key={range} value={range}>{range}</option>
                ))}
              </select>
            </div>

            {/* Timeline */}
            <div>
              <Label className="text-xs font-mono uppercase tracking-wide">
                Timeline Needed
              </Label>
              <select
                value={formData.timeline}
                onChange={(e) => setFormData({ ...formData, timeline: e.target.value })}
                className="w-full mt-1 px-3 py-2 bg-background border border-input rounded-sm text-sm"
              >
                <option value="">Select timeline...</option>
                {timelines.map(time => (
                  <option key={time} value={time}>{time}</option>
                ))}
              </select>
            </div>

            {/* Detailed Form Fields */}
            {formType === 'detailed' && (
              <>
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wide">
                    Dimensions / Size
                  </Label>
                  <Input
                    value={formData.dimensions}
                    onChange={(e) => setFormData({ ...formData, dimensions: e.target.value })}
                    placeholder="e.g., 12 feet long, 42 inches high"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label className="text-xs font-mono uppercase tracking-wide">
                    Materials Requested
                  </Label>
                  <Input
                    value={formData.materials_requested}
                    onChange={(e) => setFormData({ ...formData, materials_requested: e.target.value })}
                    placeholder="e.g., Stainless steel 304"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label className="text-xs font-mono uppercase tracking-wide">
                    Technical Specifications
                  </Label>
                  <Textarea
                    value={formData.specifications}
                    onChange={(e) => setFormData({ ...formData, specifications: e.target.value })}
                    placeholder="Any technical requirements, codes, or standards..."
                    className="mt-1 min-h-[80px]"
                  />
                </div>

                <div>
                  <Label className="text-xs font-mono uppercase tracking-wide">
                    Additional Requirements
                  </Label>
                  <Textarea
                    value={formData.technical_requirements}
                    onChange={(e) => setFormData({ ...formData, technical_requirements: e.target.value })}
                    placeholder="Certifications, finishes, installation requirements..."
                    className="mt-1 min-h-[80px]"
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* File Upload Section */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-xl font-bold uppercase">
              <Paperclip className="inline mr-2" size={20} />
              Attachments
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Upload Button */}
              <div className="border-2 border-dashed border-border rounded-sm p-6 text-center hover:border-primary transition-colors">
                <input
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                  id="file-upload"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <Upload className="mx-auto mb-2 text-muted-foreground" size={32} />
                  <p className="text-sm font-medium mb-1">
                    Click to upload files
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Drawings, photos, specifications, or references
                  </p>
                </label>
              </div>

              {/* Helpful instructions */}
              <div className="p-3 border border-green-200 bg-green-50 rounded-sm">
                <p className="text-sm text-green-800">
                  <strong>💡 Upload files anytime:</strong> Attach drawings, photos, or specifications to help us better understand your project requirements.
                </p>
              </div>

              {/* Uploaded Files List */}
              {files.length > 0 && (
                <div className="space-y-2">
                  {files.map((file) => (
                    <div key={file.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-sm">
                      <div className="flex items-center gap-3">
                        {getFileIcon(file.file_type)}
                        <div>
                          <p className="text-sm font-medium">{file.filename}</p>
                          <p className="text-xs text-muted-foreground">
                            {file.file_type} • {(file.file_size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {file.file_type}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}

              {uploading && (
                <div className="text-center py-4">
                  <Loader className="animate-spin mx-auto mb-2" size={24} />
                  <p className="text-sm text-muted-foreground">Uploading...</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex gap-3">
          {!estimateId ? (
            // When no estimate exists yet, show single submit button
            <Button
              onClick={handleSubmitNew}
              disabled={saving || !formData.project_name || !formData.description}
              className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 font-mono uppercase tracking-wider"
            >
              <Check className="mr-2" size={18} />
              {saving ? 'Submitting...' : 'Submit Estimate Request'}
            </Button>
          ) : (
            // When estimate exists, show save and submit separately
            <>
              <Button
                onClick={handleSaveProgress}
                disabled={saving || !formData.project_name || !formData.description}
                variant="outline"
                className="flex-1 font-mono uppercase tracking-wider"
              >
                {saving ? 'Saving...' : 'Save Progress'}
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={saving}
                className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 font-mono uppercase tracking-wider"
              >
                <Check className="mr-2" size={18} />
                Submit Estimate
              </Button>
            </>
          )}
        </div>
      </div>

      {/* AI Chat Sidebar */}
      <div className="lg:col-span-1">
        <Card className="border-border sticky top-4">
          <CardHeader className="cursor-pointer" onClick={() => setChatOpen(!chatOpen)}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bot className="text-primary" size={20} />
                <CardTitle className="text-lg font-bold uppercase">
                  AI Assistant
                </CardTitle>
              </div>
              <Badge variant="secondary" className="text-xs">
                {chatMessages.length} messages
              </Badge>
            </div>
          </CardHeader>
          
          {/* Liability Disclaimer */}
          <div className="px-4 pb-4">
            <div className="p-4 bg-yellow-50 border-2 border-yellow-200 rounded-sm">
              <div className="flex items-start gap-3">
                <AlertTriangle className="text-yellow-600 mt-0.5" size={18} />
                <div className="text-xs text-yellow-800 space-y-2">
                  <p className="font-bold">⚠️ IMPORTANT DISCLAIMER - READ CAREFULLY</p>
                  <p>
                    <strong>PSM Welding is NOT responsible or liable for any project details, cost estimates, material suggestions, 
                    or technical recommendations provided by this AI assistant.</strong> The AI tool is provided as a convenience 
                    to help you explore ideas and get started with your project planning.
                  </p>
                  <p>
                    <strong>YOU ASSUME ALL RISK</strong> when using AI-generated suggestions for your welding and fabrication projects. 
                    The AI assistant provides general guidance only and cannot account for specific site conditions, local codes, 
                    safety requirements, or technical specifications unique to your project.
                  </p>
                  <p className="font-semibold">
                    AI is a GREAT STARTING POINT but is NOT the &quot;be all end all&quot; for everything. Always consult with our professional 
                    staff for accurate quotes, proper engineering specifications, and safety-compliant solutions.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {chatOpen && (
            <CardContent className="p-0">
              {/* Chat Messages */}
              <div className="h-[400px] overflow-y-auto p-4 space-y-4">
                {chatMessages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] p-3 rounded-lg text-sm ${
                        msg.role === 'user'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted'
                      }`}
                    >
                      {msg.content}
                    </div>
                  </div>
                ))}
                {chatLoading && (
                  <div className="flex gap-2 justify-start">
                    <div className="bg-muted p-3 rounded-lg">
                      <Loader className="animate-spin" size={16} />
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>

              {/* Chat Input */}
              <div className="p-4 border-t border-border">
                <div className="flex gap-2">
                  <Input
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    placeholder="Ask about costs, timelines, materials..."
                    disabled={chatLoading}
                    className="text-sm"
                  />
                  <Button
                    onClick={handleSendMessage}
                    disabled={!chatInput.trim() || chatLoading}
                    size="sm"
                  >
                    <Send size={16} />
                  </Button>
                </div>
                <div className="p-3 border border-green-200 bg-green-50 rounded-sm mt-2">
                  <p className="text-sm text-green-800">
                    <strong>💬 Chat anytime:</strong> Ask about costs, materials, timelines, or any questions about your project!
                  </p>
                </div>
              </div>
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  );

  // Survey Mode (Multi-Step Wizard)
  const renderSurveyMode = () => {
    const totalSteps = 6; // Increased from 5 to include file upload step

    return (
      <div className="max-w-3xl mx-auto">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-mono uppercase tracking-wide">
              Step {currentStep} of {totalSteps}
            </span>
            <span className="text-sm text-muted-foreground">
              {Math.round((currentStep / totalSteps) * 100)}% Complete
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className="bg-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            />
          </div>
        </div>

        {/* Step Content */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-2xl font-bold uppercase">
              {currentStep === 1 && 'Project Basics'}
              {currentStep === 2 && 'Project Scope'}
              {currentStep === 3 && 'Materials & Specs'}
              {currentStep === 4 && 'File Attachments'}
              {currentStep === 5 && 'Timeline & Budget'}
              {currentStep === 6 && 'Review & Submit'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Step 1: Basics */}
            {currentStep === 1 && (
              <>
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wide">
                    Project Name *
                  </Label>
                  <Input
                    value={formData.project_name}
                    onChange={(e) => setFormData({ ...formData, project_name: e.target.value })}
                    placeholder="e.g., Custom Steel Railing"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wide">
                    Project Type *
                  </Label>
                  <select
                    value={formData.project_type}
                    onChange={(e) => setFormData({ ...formData, project_type: e.target.value })}
                    className="w-full mt-1 px-3 py-2 bg-background border border-input rounded-sm text-sm"
                  >
                    {projectTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {/* Step 2: Scope */}
            {currentStep === 2 && (
              <>
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wide">
                    Description *
                  </Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe your project..."
                    className="mt-1 min-h-[150px]"
                  />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wide">
                    Dimensions / Size
                  </Label>
                  <Input
                    value={formData.dimensions}
                    onChange={(e) => setFormData({ ...formData, dimensions: e.target.value })}
                    placeholder="e.g., 12 feet long"
                    className="mt-1"
                  />
                </div>
              </>
            )}

            {/* Step 3: Materials */}
            {currentStep === 3 && (
              <>
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wide">
                    Materials Requested
                  </Label>
                  <Input
                    value={formData.materials_requested}
                    onChange={(e) => setFormData({ ...formData, materials_requested: e.target.value })}
                    placeholder="e.g., Stainless steel 304"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wide">
                    Technical Specifications
                  </Label>
                  <Textarea
                    value={formData.specifications}
                    onChange={(e) => setFormData({ ...formData, specifications: e.target.value })}
                    placeholder="Any technical requirements..."
                    className="mt-1 min-h-[100px]"
                  />
                </div>
              </>
            )}

            {/* Step 4: File Attachments */}
            {currentStep === 4 && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground mb-4">
                  Upload any relevant files to help us better understand your project requirements.
                </p>
                
                {/* File Upload Area */}
                <div className="border-2 border-dashed border-border rounded-sm p-6 text-center hover:border-primary transition-colors">
                  <input
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                    className="hidden"
                    id="survey-file-upload"
                  />
                  <label htmlFor="survey-file-upload" className="cursor-pointer">
                    <Upload className="mx-auto mb-2 text-muted-foreground" size={32} />
                    <p className="text-sm font-medium mb-1">
                      Click to upload files
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Drawings, photos, specifications, or references
                    </p>
                  </label>
                </div>

                {/* Uploaded Files List */}
                {files.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold">Uploaded Files:</h4>
                    {files.map((file) => (
                      <div key={file.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-sm">
                        <div className="flex items-center gap-3">
                          {getFileIcon(file.file_type)}
                          <div>
                            <p className="text-sm font-medium">{file.filename}</p>
                            <p className="text-xs text-muted-foreground">{file.file_type}</p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {/* Handle delete */}}
                        >
                          <X size={16} />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                
                <p className="text-xs text-muted-foreground">
                  You can skip this step if you don't have files to upload right now.
                </p>
              </div>
            )}

            {/* Step 5: Timeline & Budget */}
            {currentStep === 5 && (
              <>
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wide">
                    Budget Range
                  </Label>
                  <select
                    value={formData.budget_range}
                    onChange={(e) => setFormData({ ...formData, budget_range: e.target.value })}
                    className="w-full mt-1 px-3 py-2 bg-background border border-input rounded-sm text-sm"
                  >
                    <option value="">Select budget...</option>
                    {budgetRanges.map(range => (
                      <option key={range} value={range}>{range}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wide">
                    Timeline Needed
                  </Label>
                  <select
                    value={formData.timeline}
                    onChange={(e) => setFormData({ ...formData, timeline: e.target.value })}
                    className="w-full mt-1 px-3 py-2 bg-background border border-input rounded-sm text-sm"
                  >
                    <option value="">Select timeline...</option>
                    {timelines.map(time => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {/* Step 6: Review */}
            {currentStep === 6 && (
              <div className="space-y-4">
                <div className="bg-muted/50 p-4 rounded-sm">
                  <h3 className="font-bold mb-3">Review Your Estimate Request</h3>
                  <dl className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">Project Name:</dt>
                      <dd className="font-medium">{formData.project_name}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">Type:</dt>
                      <dd className="font-medium">{formData.project_type}</dd>
                    </div>
                    {formData.budget_range && (
                      <div className="flex justify-between">
                        <dt className="text-muted-foreground">Budget:</dt>
                        <dd className="font-medium">{formData.budget_range}</dd>
                      </div>
                    )}
                    {formData.timeline && (
                      <div className="flex justify-between">
                        <dt className="text-muted-foreground">Timeline:</dt>
                        <dd className="font-medium">{formData.timeline}</dd>
                      </div>
                    )}
                  </dl>
                </div>
                <p className="text-sm text-muted-foreground">
                  By submitting this estimate request, our team will review your project details and get back to you with a detailed quote.
                </p>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex gap-3 pt-4">
              {currentStep > 1 && (
                <Button
                  onClick={() => setCurrentStep(currentStep - 1)}
                  variant="outline"
                  className="flex-1 font-mono uppercase tracking-wider"
                >
                  <ChevronLeft className="mr-2" size={18} />
                  Back
                </Button>
              )}
              {currentStep < totalSteps && (
                <Button
                  onClick={() => {
                    if (currentStep === 1 && !estimateId) {
                      handleCreateEstimate();
                    } else {
                      handleSaveProgress();
                    }
                    setCurrentStep(currentStep + 1);
                  }}
                  disabled={
                    (currentStep === 1 && (!formData.project_name || !formData.project_type)) ||
                    (currentStep === 2 && !formData.description)
                  }
                  className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 font-mono uppercase tracking-wider"
                >
                  Next
                  <ChevronRight className="ml-2" size={18} />
                </Button>
              )}
              {currentStep === totalSteps && (
                <Button
                  onClick={handleSubmit}
                  disabled={!estimateId}
                  className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 font-mono uppercase tracking-wider"
                >
                  <Check className="mr-2" size={18} />
                  Submit Estimate
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* AI Chat for Survey Mode */}
        <Card className="border-border mt-6">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Bot className="text-primary" size={20} />
              <CardTitle className="text-lg font-bold uppercase">
                Need Help? Ask AI Assistant
              </CardTitle>
            </div>
          </CardHeader>
          
          {/* Liability Disclaimer */}
          <div className="px-4 pb-4">
            <div className="p-4 bg-yellow-50 border-2 border-yellow-200 rounded-sm">
              <div className="flex items-start gap-3">
                <AlertTriangle className="text-yellow-600 mt-0.5" size={18} />
                <div className="text-xs text-yellow-800 space-y-2">
                  <p className="font-bold">⚠️ IMPORTANT DISCLAIMER - READ CAREFULLY</p>
                  <p>
                    <strong>PSM Welding is NOT responsible or liable for any project details, cost estimates, material suggestions, 
                    or technical recommendations provided by this AI assistant.</strong> The AI tool is provided as a convenience 
                    to help you explore ideas and get started with your project planning.
                  </p>
                  <p>
                    <strong>YOU ASSUME ALL RISK</strong> when using AI-generated suggestions for your welding and fabrication projects. 
                    The AI assistant provides general guidance only and cannot account for specific site conditions, local codes, 
                    safety requirements, or technical specifications unique to your project.
                  </p>
                  <p className="font-semibold">
                    AI is a GREAT STARTING POINT but is NOT the &quot;be all end all&quot; for everything. Always consult with our professional 
                    staff for accurate quotes, proper engineering specifications, and safety-compliant solutions.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <CardContent>
            <div className="flex gap-2">
              <Input
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                placeholder="Ask about this step..."
                disabled={!estimateId || chatLoading}
                className="text-sm"
              />
              <Button
                onClick={handleSendMessage}
                disabled={!chatInput.trim() || !estimateId || chatLoading}
                size="sm"
              >
                <Send size={16} />
              </Button>
            </div>
            {chatMessages.length > 0 && (
              <div className="mt-4 max-h-[200px] overflow-y-auto space-y-2">
                {chatMessages.slice(-3).map((msg, idx) => (
                  <div
                    key={idx}
                    className={`p-2 rounded text-xs ${
                      msg.role === 'user' ? 'bg-primary/10 ml-4' : 'bg-muted mr-4'
                    }`}
                  >
                    {msg.content}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <div className="p-8">
      {renderForm()}
    </div>
  );
};

export default EstimateBuilder;